import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useTierAccess } from "@/hooks/useTierAccess";
import { useToast } from "@/hooks/use-toast";

interface ManagerProtectedRouteProps {
  children: React.ReactNode;
}

export const ManagerProtectedRoute = ({ children }: ManagerProtectedRouteProps) => {
  const { loading: authLoading, isSignedIn } = useAuth();
  const { loading: rolesLoading, isManager } = useUserRoles();
  const { isLoading: tierLoading, canAccessManagerDashboard, tierDisplayName } = useTierAccess();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!authLoading && !rolesLoading && !tierLoading) {
      if (!isSignedIn) {
        navigate("/auth/sign-in?source=manager-guard&next=%2Fdashboard%2Flive", { replace: true });
      } else if (!isManager) {
        toast({
          title: "Access Denied",
          description: "You need manager privileges to access the dashboard.",
          variant: "destructive",
        });
        navigate("/train", { replace: true });
      } else if (!canAccessManagerDashboard) {
        toast({
          title: "Upgrade Required",
          description: `The Manager Dashboard is available on the Pro plan. You're currently on ${tierDisplayName}. Upgrade to unlock full access.`,
          variant: "destructive",
        });
        // Redirect to train instead of choose-plan; user can upgrade via /pricing when ready
        navigate("/train", { replace: true });
      }
    }
  }, [authLoading, rolesLoading, tierLoading, isSignedIn, isManager, canAccessManagerDashboard, tierDisplayName, navigate, toast]);

  if (authLoading || rolesLoading || tierLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse text-2xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
            Verifying permissions...
          </div>
        </div>
      </div>
    );
  }

  if (!isSignedIn || !isManager || !canAccessManagerDashboard) {
    return null;
  }

  return <>{children}</>;
};
